/**
 * Single fetch wrapper used by every component.
 *
 * Why centralize it:
 *  - Always sends cookies (credentials: 'include') so admin sessions work.
 *  - Auto-redirects to /login on 401 with code AUTH_REQUIRED.
 *  - Normalizes errors to { ok: false, error, errors } shape.
 *  - Parses JSON safely (returns { ok: false } if body isn't JSON).
 */

const BASE = ''; // proxied in dev via CRA, same-origin in prod

class ApiError extends Error {
  constructor(message, { status, code, errors } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.errors = errors;
  }
}

async function parseBody(res) {
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    try { return await res.json(); } catch { return null; }
  }
  try { return await res.text(); } catch { return null; }
}

async function request(method, path, { body, query, signal, headers } = {}) {
  let url = BASE + path;
  if (query && typeof query === 'object') {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
    });
    const qs = params.toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }

  const init = {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(headers || {}),
    },
    signal,
  };

  if (body !== undefined && body !== null) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  let res;
  try {
    res = await fetch(url, init);
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError('Network error. Check your connection.', { status: 0 });
  }

  const data = await parseBody(res);
  const isJson = data && typeof data === 'object';

  // session expired or not logged in
  if (res.status === 401 && isJson && data.code === 'AUTH_REQUIRED') {
    if (!window.location.pathname.startsWith('/login')) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.replace(`/login?next=${next}`);
    }
    throw new ApiError('Authentication required', {
      status: 401,
      code: 'AUTH_REQUIRED',
    });
  }

  if (!res.ok) {
    const message = (isJson && (data.error || data.message)) || `Request failed (${res.status})`;
    throw new ApiError(message, {
      status: res.status,
      code: isJson ? data.code : undefined,
      errors: isJson ? data.errors : undefined,
    });
  }

  return isJson ? data : { ok: true, raw: data };
}

const api = {
  get:    (path, opts) => request('GET',    path, opts),
  post:   (path, body, opts = {}) => request('POST',   path, { ...opts, body }),
  patch:  (path, body, opts = {}) => request('PATCH',  path, { ...opts, body }),
  put:    (path, body, opts = {}) => request('PUT',    path, { ...opts, body }),
  delete: (path, opts) => request('DELETE', path, opts),
};

export { api, ApiError };
export default api;